import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../apiClient";

export type DraftFeedbackSentiment = "like" | "dislike";

export type DraftFeedbackSummary = {
  sentiment: DraftFeedbackSentiment;
  reason_category?: string | null;
  feedback_text?: string | null;
  created_at?: string;
};

export type DraftFeedbackResponse = {
  message_draft_uid: string;
  feedback: DraftFeedbackSummary | null;
};

export type DraftFeedbackCreate = {
  sentiment: DraftFeedbackSentiment;
  reason_category?: string;
  feedback_text?: string;
};

export const draftFeedbackQueryKey = (draftUid: string) =>
  ["draft-feedback", draftUid] as const;

export const useDraftFeedbackQuery = (
  draftUid: string | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: draftFeedbackQueryKey(draftUid ?? ""),
    queryFn: async () => {
      const { data } = await apiClient.get<DraftFeedbackResponse>(
        "/email/drafts/feedback",
        { params: { draft_uid: draftUid } },
      );
      return data;
    },
    enabled: Boolean(draftUid) && enabled,
  });

export const useSubmitDraftFeedbackMutation = (draftUid: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: DraftFeedbackCreate) => {
      const { data } = await apiClient.post<DraftFeedbackResponse>(
        "/email/drafts/feedback",
        body,
        { params: { draft_uid: draftUid } },
      );
      return data;
    },
    onSuccess: (data) => {
      if (draftUid) {
        queryClient.setQueryData(draftFeedbackQueryKey(draftUid), data);
      }
    },
  });
};
